/**
 * DemoHistoryDrawer Component
 *
 * Displays session history of flashcard review grades in a responsive drawer.
 * - Mobile: Bottom drawer (max 30% vh)
 * - Desktop: Right sidebar (384px width)
 *
 * Follows calendar drawer conventions for consistent UX.
 */

import { X, Clock } from 'lucide-react'

interface DemoReview {
  id: string
  timestamp: number
  grade: 0 | 1 | 2 | 3 | 4 | 5
  card: {
    frontSpanish: string
    backEnglish: string
  }
}

interface DemoHistoryDrawerProps {
  isOpen: boolean
  onClose: () => void
  reviews: DemoReview[]
}

// Grade configuration for display - matches index route GRADE_BUTTONS colors exactly
const GRADE_CONFIG = {
  0: { label: 'Forgot', textColor: 'text-red-600', bgColor: 'bg-red-600' },
  1: { label: 'Hard', textColor: 'text-orange-400', bgColor: 'bg-orange-400' },
  2: { label: 'OK', textColor: 'text-yellow-600', bgColor: 'bg-yellow-600' },
  3: { label: 'Good', textColor: 'text-blue-600', bgColor: 'bg-blue-600' },
  4: { label: 'Easy', textColor: 'text-teal-600', bgColor: 'bg-teal-600' },
  5: { label: 'Perfect', textColor: 'text-terminal-green', bgColor: 'bg-green-600' },
} as const

// Helper: Time ago formatting
function getTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000)
  if (seconds < 10) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ago`
}

// ReviewItem Subcomponent
function ReviewItem({ review, isLatest }: { review: DemoReview; isLatest: boolean }) {
  const gradeConfig = GRADE_CONFIG[review.grade]
  const timeAgo = getTimeAgo(review.timestamp)

  return (
    <div className={`
      border-2 p-3
      ${isLatest ? 'border-terminal-green' : 'border-sky-blue'}
    `}>
      {/* Header: Time + Grade */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-trail-tan font-mono">{timeAgo}</span>
        <span className={`text-xs font-bold font-mono ${gradeConfig.textColor}`}>
          {review.grade} - {gradeConfig.label}
        </span>
      </div>

      {/* Card Content Preview */}
      <div className="text-xs font-mono space-y-1">
        <div className="text-white">{review.card.frontSpanish}</div>
        <div className="text-trail-tan">{review.card.backEnglish}</div>
      </div>

      {/* Latest Badge */}
      {isLatest && (
        <div className="mt-2 text-[10px] text-terminal-green font-bold animate-pulse">
          ▶ LATEST REVIEW
        </div>
      )}
    </div>
  )
}

export function DemoHistoryDrawer({ isOpen, onClose, reviews }: DemoHistoryDrawerProps) {
  // Calculate session statistics
  const sessionStats = reviews.length > 0 ? {
    averageGrade: reviews.reduce((sum, r) => sum + r.grade, 0) / reviews.length,
    totalTime: Math.floor((Date.now() - reviews[0].timestamp) / 1000),
  } : null

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    if (mins === 0) return `${secs}s`
    return `${mins}m ${secs}s`
  }

  const getAverageGradeLabel = (avg: number) => {
    if (avg < 2) return 'Needs Practice'
    if (avg < 3) return 'Learning'
    if (avg < 4) return 'Good Progress'
    if (avg < 4.5) return 'Strong Recall'
    return 'Mastered'
  }

  // Early return if not open - unmounts everything including backdrop
  if (!isOpen) return null

  return (
    <>
      {/* Backdrop - Hidden on desktop (lg:hidden) */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
        onClick={onClose}
        aria-label="Close history drawer"
      />

      {/* Drawer - Mobile: Bottom (max-h-[30vh]), Desktop: Right Sidebar */}
      <div className="fixed top-0 right-0 bottom-0 w-full sm:w-96 bg-black border-l-2 border-sky-blue overflow-y-auto z-50 shadow-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 bg-black border-b-2 border-sky-blue p-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-sky-blue font-mono tracking-wider">
            [SESSION HISTORY]
          </h3>
          <button
            onClick={onClose}
            className="text-trail-tan hover:text-terminal-green transition-colors"
            type="button"
            aria-label="Close history drawer"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="p-4 space-y-4">
          {reviews.length === 0 ? (
            // Empty State
            <div className="text-center py-12 text-trail-tan font-mono">
              <p className="mb-2">No reviews yet</p>
              <p className="text-xs">Rate the card to see your history!</p>
            </div>
          ) : (
            <>
              {/* Session Summary */}
              {sessionStats && (
                <div className="terminal-border border-sky-blue p-4 bg-gray-900/50">
                  {/* Stats Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs font-mono text-gray-400">
                      [DEMO SESSION]
                    </div>
                    <div className="flex items-center gap-2 text-xs font-mono text-trail-tan">
                      <Clock className="w-3 h-3" />
                      {formatDuration(sessionStats.totalTime)}
                    </div>
                  </div>

                  {/* Average Grade */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-mono text-gray-400">Average Confidence</span>
                    <div className="flex flex-col items-end">
                      <span className="text-terminal-green font-mono text-lg font-bold">
                        {sessionStats.averageGrade.toFixed(1)}
                      </span>
                      <span className="text-xs font-mono text-gray-400">
                        {getAverageGradeLabel(sessionStats.averageGrade)}
                      </span>
                    </div>
                  </div>

                  {/* Grade Distribution Visualization */}
                  <div className="mt-3">
                    <div className="text-xs font-mono text-gray-400 mb-2">
                      [CONFIDENCE DISTRIBUTION]
                    </div>
                    <div className="flex gap-1 h-16">
                      {[0, 1, 2, 3, 4, 5].map((grade) => {
                        const count = reviews.filter((r) => r.grade === grade).length
                        const percentage = (count / reviews.length) * 100
                        const gradeConfig = GRADE_CONFIG[grade as keyof typeof GRADE_CONFIG]

                        return (
                          <div key={grade} className="flex-1 flex flex-col justify-end">
                            <div
                              className={`${gradeConfig.bgColor} transition-all duration-300 min-h-[2px]`}
                              style={{ height: `${percentage}%` }}
                              title={`Grade ${grade}: ${count} cards (${percentage.toFixed(0)}%)`}
                            />
                            <div className="text-xs text-center text-gray-500 font-mono mt-1">
                              {grade}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="mt-3 pt-3 border-t border-gray-700/50 flex items-center justify-between text-xs font-mono">
                    <span className="text-gray-500">0-1: Hard</span>
                    <span className="text-gray-500">2-3: Medium</span>
                    <span className="text-gray-500">4-5: Easy</span>
                  </div>
                </div>
              )}

              {/* Individual Review Items */}
              <div className="space-y-3">
                <div className="text-xs font-mono text-gray-400 uppercase tracking-wider">
                  [Individual Reviews]
                </div>
                {reviews.map((review, idx) => (
                  <ReviewItem
                    key={review.id}
                    review={review}
                    isLatest={idx === 0}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
